import React, { useEffect, useMemo } from "react";
import { Route, Routes, useParams, useLocation, useNavigate, Outlet } from "react-router-dom";
import { EventContextProvider, useEventContext } from "../../context/EventContext";
import { UserContextProvider, useUserContext } from "../../context/UserContext";
import { NotificationsContextProvider } from "../../context/NotificationsContext";
import { ApplicationContextProvider, HIDDEN_PAGES } from "../../context/ApplicationContext";
import { TEST_USER, TYPE_HYBRID } from "../../constants/common";
import Header from "../../components/Header";
import { StreamContextProvider } from "../../context/StreamContext";
import PageComponent from "../../components/PageComponent";
import NotFound from "../NotFound";
import PollResultPage from "../PollResultPage";
import { CommentsContextProvider } from "../../context/CommentsContext";
import { QuestionsContextProvider } from "../../context/QuestionsContext";
import UIKitPage from "../UIKitPage";
import { PollsProvider } from "../../context/PollsContext";
import { ModalContextProvider } from "../../context/ModalContext";
import { StatisticContextProvider } from "../../context/StatisticContext";
import { QuizProvider } from "../../context/QuizContext";
import { useLocaleContext } from "../../context/LocaleContext";
import PlayerContainer from "../../components/Player/Container";
import Theme from "./Theme";
import { ShopContextProvider } from "../../context/ShopContext";
import EventNotificationModal from "../../components/EventNotificationModal";
import Polls from "../../components/Polls";
import { usePlayerContext } from "../../context/PlayerContext";
import Authentication from "../Authentication";
import ActivityPreviewPage from "../ActivityPreviewPage";

const watchOnlyPages = [
    {
        path: 'poll-result/:pollID',
        template: <PollResultPage />
    },
    {
        path: 'poll/:pollID',
        template: <ActivityPreviewPage type={'poll'} />
    },
    {
        path: 'notification/:notificationID',
        template: <ActivityPreviewPage type={'notification'} />
    },
    {
        path: 'quiz/:quizID/:pollIndex',
        template: <ActivityPreviewPage type={'quiz'} />
    },
];

const pageLoader = () => {
    window.scrollTo({ top: 0 });
};

const EventRoutes = ({ pages }) => {

    return (
        <Routes>
            {pages?.map((page) => (
                <Route key={page.path} path={page.path} element={<PageComponent page={page} />} />
            ))}
            <Route path={`ui-kit`} element={<UIKitPage />} />
            <Route path={`*`} element={<NotFound />} />
        </Routes>
    );
};

export const EventPageRoute = () => {
    const { pages, event } = useEventContext();
    const { watchKey } = useParams();
    const { fullScreen } = usePlayerContext();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { participationType } = useUserContext();
    const { getProperty } = useLocaleContext();

    const filteredPages = useMemo(() => (
        pages?.filter((page) => (
            page.path && (page.participationType === TYPE_HYBRID || page.participationType === participationType)
        )) || []
    ), [pages, participationType]);

    const visiblePages = useMemo(() => {
        return filteredPages.filter(({ visible, template }) => visible && !HIDDEN_PAGES.includes(template));
    }, [filteredPages]);

    const defaultPage = visiblePages.find(({ defaultType }) => (
        defaultType === participationType || defaultType === TYPE_HYBRID
    )) || visiblePages[0];

    const defaultPagePath = `/event/${watchKey}/${defaultPage?.path}`;

    useEffect(() => {
        document.title = getProperty(event, 'name');
        document.body.setAttribute('spellcheck', 'false');
    }, [event]);

    useEffect(() => {
        const hasSelectedPage = !!pathname.split(watchKey)[1];

        if (!participationType || hasSelectedPage) {
            return;
        }

        navigate(defaultPagePath + search);
    }, [pathname, participationType, filteredPages]);

    return (
        <>
            <Theme />
            <ModalContextProvider>
                <Header defaultPagePath={defaultPagePath} />
                <EventNotificationModal />
                {!fullScreen && <Polls isFixedDisplay={true} />}
            </ModalContextProvider>
            <div className={`page-body`} id="page-body">
                <EventRoutes
                    pages={filteredPages}
                />
            </div>
        </>
    );
}

const ProviderWrapper = ({ pageName, preview }) => (
    <ApplicationContextProvider>
        <ShopContextProvider>
            <StatisticContextProvider>
                <CommentsContextProvider>
                    <QuestionsContextProvider>

                        <Outlet />

                        <ModalContextProvider>
                            <PlayerContainer isHallsPage={pageName === "halls"} />
                        </ModalContextProvider>

                    </QuestionsContextProvider>
                </CommentsContextProvider>
            </StatisticContextProvider>
        </ShopContextProvider>
    </ApplicationContextProvider>
)

export const WrapperEventPageRoute = () => {
    const { pathname, search } = useLocation();
    const [_, participationType, watchKey, pageName] = pathname.split('/');
    const params = new URLSearchParams(search);
    const preview = !!params.get('preview');

    useEffect(() => {
        pageLoader();
    }, [pageName])

    const isPreview = preview || pageName === 'watch-only';

    return (
        <EventContextProvider participationType={participationType} watchKey={watchKey}>
            <StreamContextProvider>
                <PollsProvider>
                    <NotificationsContextProvider preview={isPreview}>
                        <UserContextProvider preview={isPreview}>
                            <QuizProvider pageName={pageName} preview={isPreview}>
                                <Routes>
                                    <Route
                                        path="/event/:watchKey/watch-only"
                                    >
                                        {watchOnlyPages.map((page) => (
                                            <Route key={page.path} path={page.path} element={page.template} />
                                        ))}
                                    </Route>
                                    <Route path={'/event/:watchKey/auth'} element={<Authentication force preview={preview} />} />
                                    <Route element={<ProviderWrapper pageName={pageName} preview={preview} />}>
                                        <Route
                                            path={`/event/:watchKey/*`}
                                            element={<EventPageRoute />}
                                        />
                                    </Route>
                                </Routes>
                            </QuizProvider>
                        </UserContextProvider>
                    </NotificationsContextProvider>
                </PollsProvider>
            </StreamContextProvider>
        </EventContextProvider>
    );
};
